import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "../utils/api";
import logo from "../assets/shoe.png";

const fmtKesh = (n) =>
  `${Number(n || 0).toLocaleString("en-KE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} Kesh`;

const localTodayISO = () => {
  const d = new Date();
  const pad = (x) => String(x).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// parse like "2025-08-09 10:52:06" or "2025-08-09"
const asISODate = (val) => {
  if (!val) return null;
  const t = String(val).trim();
  // keep only date part
  const datePart = t.split(" ")[0];
  const d = new Date(datePart);
  return isNaN(d.getTime()) ? null : d;
};

const inRangeInclusive = (d, startISO, endISO) => {
  const dd = asISODate(d);
  if (!dd) return false;
  const s = new Date(startISO);
  const e = new Date(endISO);
  s.setHours(0, 0, 0, 0);
  e.setHours(23, 59, 59, 999);
  return dd >= s && dd <= e;
};

const StockTrackingReport = () => {
  const [data, setData] = useState(null); // raw API data
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("day");
  const [startDate, setStartDate] = useState(localTodayISO());
  const [endDate, setEndDate] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // the currently displayed scope
  const [scope, setScope] = useState({
    type: "day",
    date: localTodayISO(),
    start: null,
    end: null,
  });

  const reportRef = useRef();

  // fetch for a single day
  useEffect(() => {
    if (filterType !== "day" || !startDate) return;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await axios.get("/stock-tracking.php", {
          params: { date: startDate },
        });
        if (res.data?.report) {
          setData(res.data);
          setScope({ type: "day", date: startDate, start: null, end: null });
        } else {
          throw new Error("No report data returned");
        }
      } catch (e) {
        console.error(e);
        setData(null);
        setError("Failed to load stock report.");
      } finally {
        setLoading(false);
      }
    })();
  }, [filterType, startDate]);

  const validRange =
    filterType === "range" &&
    startDate &&
    endDate &&
    new Date(endDate) >= new Date(startDate);

  const fetchRange = async () => {
    if (!validRange) {
      setError("Please choose a valid start and end date (end ≥ start).");
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get("/stock-tracking.php", {
        params: {
          start: startDate,
          end: endDate,
          // also send alternates in case PHP expects these
          start_date: startDate,
          end_date: endDate,
          range: 1,
        },
      });
      if (res.data?.report) {
        setData(res.data);
        setScope({ type: "range", start: startDate, end: endDate, date: null });
      } else {
        throw new Error("No report data for the selected range");
      }
    } catch (e) {
      console.error(e);
      setData(null);
      setError("Failed to load range report.");
    } finally {
      setLoading(false);
    }
  };

  const onChangeFilterType = (v) => {
    setFilterType(v);
    setError(null);
    setSearchTerm("");
    if (v === "day") {
      setEndDate("");
      if (!startDate) setStartDate(localTodayISO());
    } else {
      setEndDate(startDate || localTodayISO());
    }
  };

  // rows filtered by search AND by selected scope (range/day)
  const filteredRows = useMemo(() => {
    if (!data?.report) return [];
    let rows = data.report;

    // apply date-range constraint strictly on last_sold_at
    if (scope.type === "range" && scope.start && scope.end) {
      rows = rows.filter((r) => inRangeInclusive(r.last_sold_at, scope.start, scope.end));
    } else if (scope.type === "day" && scope.date) {
      rows = rows.filter((r) => inRangeInclusive(r.last_sold_at, scope.date, scope.date));
    }

    // then search
    const q = (searchTerm || "").toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (item) =>
        item.product_name?.toLowerCase().includes(q) ||
        String(item.size ?? "").includes(q) ||
        item.color?.toLowerCase().includes(q)
    );
  }, [data, searchTerm, scope]);

  // recompute summary from filtered rows (so totals match what you see)
  const computedSummary = useMemo(() => {
    const totals = {
      total_units_sold: 0,
      total_sales_kesh: 0,
      generated_at: data?.summary?.generated_at ?? "",
    };
    for (const r of filteredRows) {
      totals.total_units_sold += Number(r.sold_quantity || 0);
      totals.total_sales_kesh += Number(r.total_sales || 0);
    }
    return totals;
  }, [filteredRows, data?.summary?.generated_at]);

  const handlePrint = () => window.print();

  const getAddressBlock = (shop_id) => {
    const id = Number(shop_id);
    switch (id) {
      case 1:
        return (
          <>
            <p>
              <strong>NAIROBI, KENYA</strong>
            </p>
            <p>Kamukunji Market</p>
            <p>Building: Marpet Plaza, First Floor, Shop No. 20</p>
            <p>Contact: +254740357257/+254745833965</p>
          </>
        );
      case 2:
        return (
          <>
            <p>
              <strong>NAIROBI, KENYA</strong>
            </p>
            <p>Kamukunji Market</p>
            <p>Building: Marpet Plaza, Second Floor, Shop No. 46</p>
            <p>Contact: +254740357257/+254745833965</p>
          </>
        );
      default:
        return <p className="text-red-500">⚠️ location info unavailable</p>;
    }
  };

  return (
    <div className="p-4 print:p-0">
      {/* filters */}
      <div className="flex flex-wrap items-center justify-between mb-4 print:hidden gap-2">
        <div className="flex items-center gap-2">
          <select
            value={filterType}
            onChange={(e) => onChangeFilterType(e.target.value)}
            className="border px-2 py-1 rounded"
          >
            <option value="day">day</option>
            <option value="range">date range</option>
          </select>

          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="border rounded px-2 py-1"
          />

          {filterType === "range" && (
            <>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="border rounded px-2 py-1"
                min={startDate || undefined}
              />
              <button
                onClick={fetchRange}
                disabled={!validRange || loading}
                className={`px-3 py-1 rounded text-white ${
                  !validRange || loading
                    ? "bg-blue-300 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700"
                }`}
                title={
                  validRange
                    ? "Load"
                    : "Pick a valid start & end date (end ≥ start)"
                }
              >
                {loading ? "loading..." : "load"}
              </button>
            </>
          )}
        </div>

        <input
          type="text"
          placeholder="🔍 search product, size, color"
          className="border px-2 py-1 rounded w-64"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        <button
          onClick={handlePrint}
          className="bg-blue-600 text-white px-4 py-1 rounded hover:bg-blue-700"
        >
          🖨️ print a4 report
        </button>
      </div>

      {/* report */}
      <div ref={reportRef} className="bg-white p-6 rounded shadow print:shadow-none print:p-6">
        {error ? (
          <p className="text-center text-red-600">{error}</p>
        ) : !data ? (
          <p className="text-center text-gray-500">
            {loading ? "loading report..." : "choose a date and load the report"}
          </p>
        ) : (
          <>
            {/* header */}
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-start space-x-2">
                <img src={logo} alt="logo" className="h-12 w-12" />
                <div>
                  <h2 className="text-base font-bold uppercase">
                    {data.shop_name || `shop #${data.shop_id}`}
                  </h2>
                  <p className="text-sm text-gray-500">shop id: {data.shop_id}</p>
                </div>
              </div>
              <div className="text-right text-sm">
                {data?.shop_id ? getAddressBlock(data.shop_id) : <p>⏳ loading address...</p>}
              </div>
            </div>

            {/* report title */}
            <div className="text-center mb-6">
              <h3 className="text-xl font-semibold uppercase">stock tracking report</h3>
              <p className="text-sm text-gray-500 mb-1">
                {scope.type === "range" && scope.start && scope.end
                  ? `from ${scope.start} to ${scope.end}`
                  : `date: ${scope.date}`}
              </p>
              <p className="text-xs text-gray-400">
                generated on {computedSummary.generated_at}
              </p>
            </div>

            {/* table */}
            <table className="w-full border-collapse border border-gray-300 text-xs print:text-[11px]">
              <thead className="bg-gray-100">
                <tr>
                  <th className="border px-2 py-1">#</th>
                  <th className="border px-2 py-1">product</th>
                  <th className="border px-2 py-1">size</th>
                  <th className="border px-2 py-1">color</th>
                  <th className="border px-2 py-1">stock</th>
                  <th className="border px-2 py-1">sold</th>
                  <th className="border px-2 py-1">unit</th>
                  <th className="border px-2 py-1">price (Kesh)</th>
                  <th className="border px-2 py-1">total sales (Kesh)</th>
                  <th className="border px-2 py-1">last sold</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="text-center py-3 text-gray-500">
                      no records found.
                    </td>
                  </tr>
                ) : (
                  filteredRows.map((item, index) => (
                    <tr key={item.product_id ?? `${item.product_name}-${index}`}>
                      <td className="border px-2 py-1 text-center">{index + 1}</td>
                      <td className="border px-2 py-1">{item.product_name}</td>
                      <td className="border px-2 py-1 text-center">{item.size}</td>
                      <td className="border px-2 py-1 text-center">{item.color}</td>
                      <td className="border px-2 py-1 text-center">{item.current_stock}</td>
                      <td className="border px-2 py-1 text-center">{item.sold_quantity}</td>
                      <td className="border px-2 py-1 text-center">{item.unit}</td>
                      <td className="border px-2 py-1 text-right">
                        {fmtKesh(item.unit_price_sold)}
                      </td>
                      <td className="border px-2 py-1 text-right font-semibold">
                        {fmtKesh(item.total_sales)}
                      </td>
                      <td className="border px-2 py-1 text-center">
                        {item.last_sold_at || "—"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>

              {filteredRows.length > 0 && (
                <tfoot className="bg-gray-50 font-semibold">
                  <tr>
                    <td colSpan="5" className="border px-2 py-2 text-right">
                      total units sold
                    </td>
                    <td className="border px-2 py-2 text-center">
                      {computedSummary.total_units_sold}
                    </td>
                    <td colSpan="2" className="border px-2 py-2 text-right">
                      total sales
                    </td>
                    <td className="border px-2 py-2 text-right text-green-700">
                      {fmtKesh(computedSummary.total_sales_kesh)}
                    </td>
                    <td className="border"></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </>
        )}
      </div>
    </div>
  );
};

export default StockTrackingReport;
